import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  IconButton,
  Avatar,
  Alert,
  AlertTitle,
  alpha,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Group,
  Add,
  Edit,
  Delete,
  Block,
  Business,
  FilterList,
  Refresh,
  SupervisorAccount,
  Person,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectCurrentUser, selectCurrentCompany } from '@/store/slices/authSlice';
import { api } from '@/services/api';
import toast from 'react-hot-toast';

interface Company {
  id: string;
  name: string;
  tradingName?: string;
  document: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  lastLoginAt?: string;
  companyId: string;
  companyName?: string;
}

const UsersPage: React.FC = () => {
  const theme = useTheme();
  const currentUser = useAppSelector(selectCurrentUser);
  const currentCompany = useAppSelector(selectCurrentCompany);

  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('all');

  const isMaster = currentUser?.role === 'master';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Carregar empresas (apenas para master)
      if (isMaster) {
        try {
          const companiesRes = await api.get('/companies');
          const companiesData = companiesRes?.data?.data || companiesRes?.data || [];
          setCompanies(Array.isArray(companiesData) ? companiesData : []);
        } catch (e: any) {
          console.error('Erro ao carregar empresas:', e);
          toast.error('Erro ao carregar empresas');
          setCompanies([]);
        }
      }

      // Carregar usuários
      try {
        const usersRes = await api.get('/users');
        const usersData = usersRes?.data?.data || usersRes?.data || [];
        setUsers(Array.isArray(usersData) ? usersData : []);
      } catch (e: any) {
        console.error('Erro ao carregar usuários:', e);
        toast.error('Erro ao carregar usuários');
        setUsers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const getRoleChip = (role: string) => {
    switch (role) {
      case 'master':
        return <Chip icon={<SupervisorAccount sx={{ fontSize: 14 }} />} label="Master" color="secondary" size="small" />;
      case 'admin':
        return <Chip label="Admin" color="error" size="small" />;
      case 'manager':
        return <Chip label="Gerente" color="warning" size="small" />;
      case 'operator':
        return <Chip label="Operador" color="primary" size="small" />;
      case 'viewer':
        return <Chip label="Visualizador" color="default" size="small" />;
      default:
        return <Chip label={role} size="small" />;
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <Chip
            icon={<CheckCircle sx={{ fontSize: 14 }} />}
            label="Ativo"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.12),
              color: 'success.main',
              '& .MuiChip-icon': { color: 'success.main' },
            }}
          />
        );
      case 'inactive':
        return (
          <Chip
            icon={<Cancel sx={{ fontSize: 14 }} />}
            label="Inativo"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.12),
              color: 'text.secondary',
              '& .MuiChip-icon': { color: 'text.secondary' },
            }}
          />
        );
      case 'blocked':
        return (
          <Chip
            icon={<Block sx={{ fontSize: 14 }} />}
            label="Bloqueado"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.12),
              color: 'error.main',
              '& .MuiChip-icon': { color: 'error.main' },
            }}
          />
        );
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    try {
      return new Date(dateString).toLocaleString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const filteredUsers = selectedCompanyId === 'all'
    ? users
    : users.filter(u => u.companyId === selectedCompanyId);

  const selectedCompany = companies.find(c => c.id === selectedCompanyId);

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={40} />
        <Skeleton variant="rounded" height={60} sx={{ my: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <Group sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gerenciar Usuários
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<Add />}>
            Novo Usuário
          </Button>
        </Box>
      </Box>

      {/* Seletor de Empresa (apenas para Master) */}
      {isMaster && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FilterList color="action" />
              </Grid>
              <Grid item xs>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtrar por Empresa</InputLabel>
                  <Select
                    value={selectedCompanyId}
                    label="Filtrar por Empresa"
                    onChange={(e) => setSelectedCompanyId(e.target.value)}
                  >
                    <MenuItem value="all">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business fontSize="small" color="action" />
                        <span>Todas as Empresas</span>
                        <Chip label={users.length} size="small" sx={{ ml: 1 }} />
                      </Box>
                    </MenuItem>
                    <Divider />
                    {companies.map((company) => {
                      const count = users.filter(u => u.companyId === company.id).length;
                      return (
                        <MenuItem key={company.id} value={company.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Business fontSize="small" color="primary" />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight={500}>
                                {company.tradingName || company.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                CNPJ: {company.document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
                              </Typography>
                            </Box>
                            <Chip label={`${count} usuário${count !== 1 ? 's' : ''}`} size="small" />
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  {filteredUsers.length} usuário{filteredUsers.length !== 1 ? 's' : ''} encontrado{filteredUsers.length !== 1 ? 's' : ''}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Banner da Empresa Selecionada */}
      {selectedCompanyId !== 'all' && selectedCompany && (
        <Alert
          severity="info"
          icon={<Business />}
          sx={{
            mb: 3,
            backgroundColor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            '& .MuiAlert-icon': { color: theme.palette.info.main },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>
            Exibindo usuários de: {selectedCompany.tradingName || selectedCompany.name}
          </AlertTitle>
          <Typography variant="body2" color="text.secondary">
            CNPJ: {selectedCompany.document.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
          </Typography>
        </Alert>
      )}

      {/* Tabela de Usuários */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Usuário</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>E-mail</TableCell>
              {(isMaster && selectedCompanyId === 'all') && (
                <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
              )}
              <TableCell sx={{ fontWeight: 600 }}>Perfil</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Último Acesso</TableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    Nenhum usuário encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: user.role === 'master'
                            ? theme.palette.secondary.main
                            : user.role === 'admin'
                            ? theme.palette.error.main
                            : theme.palette.primary.main,
                          fontSize: '0.9rem',
                        }}
                      >
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name}
                        </Typography>
                        {user.role === 'master' && (
                          <Typography variant="caption" color="secondary.main">
                            Super Administrador
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.email}</Typography>
                  </TableCell>
                  {(isMaster && selectedCompanyId === 'all') && (
                    <TableCell>
                      <Tooltip title={`ID: ${user.companyId}`}>
                        <Chip
                          icon={<Business sx={{ fontSize: 14 }} />}
                          label={user.companyName || 'N/A'}
                          size="small"
                          variant="outlined"
                          sx={{ maxWidth: 180 }}
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>{getRoleChip(user.role)}</TableCell>
                  <TableCell>{getStatusChip(user.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(user.lastLoginAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Editar">
                      <IconButton size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={user.status === 'blocked' ? 'Desbloquear' : 'Bloquear'}>
                      <IconButton size="small" color="warning">
                        <Block fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" color="error" disabled={user.role === 'master'}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Legenda */}
      {isMaster && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Legenda de perfis:
          </Typography>
          {getRoleChip('master')}
          {getRoleChip('admin')}
          {getRoleChip('manager')}
          {getRoleChip('operator')}
          {getRoleChip('viewer')}
        </Box>
      )}
    </Box>
  );
};

export default UsersPage;
